/**
 * Error Types for Works Lite Application
 *
 * Provides comprehensive error handling types for various failure scenarios
 * including API errors, network errors, validation errors, and more.
 */

export enum ErrorType {
  NETWORK = 'NETWORK',
  API = 'API',
  VALIDATION = 'VALIDATION',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  NOT_FOUND = 'NOT_FOUND',
  SERVER = 'SERVER',
  TIMEOUT = 'TIMEOUT',
  UNKNOWN = 'UNKNOWN',
}

export interface ErrorDetails {
  type: ErrorType;
  message: string;
  statusCode?: number;
  endpoint?: string;
  timestamp: Date;
  details?: Record<string, unknown>;
}

/**
 * Base Application Error
 * Extends native Error with additional context for better debugging
 */
export class AppError extends Error {
  public readonly type: ErrorType;
  public readonly statusCode?: number;
  public readonly endpoint?: string;
  public readonly timestamp: Date;
  public readonly details?: Record<string, unknown>;

  constructor(
    message: string,
    type: ErrorType = ErrorType.UNKNOWN,
    statusCode?: number,
    endpoint?: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.statusCode = statusCode;
    this.endpoint = endpoint;
    this.timestamp = new Date();
    this.details = details;

    // Maintains proper stack trace for where error was thrown (V8 engines only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }

  toJSON(): ErrorDetails {
    return {
      type: this.type,
      message: this.message,
      statusCode: this.statusCode,
      endpoint: this.endpoint,
      timestamp: this.timestamp,
      details: this.details,
    };
  }
}

/**
 * Network Error - Connection issues, offline, etc.
 */
export class NetworkError extends AppError {
  constructor(message = 'Network connection failed. Please check your internet connection.') {
    super(message, ErrorType.NETWORK);
    this.name = 'NetworkError';
  }
}

/**
 * API Error - Server responded with error status
 */
export class ApiError extends AppError {
  constructor(
    message: string,
    statusCode: number,
    endpoint?: string,
    details?: Record<string, unknown>,
  ) {
    super(message, ErrorType.API, statusCode, endpoint, details);
    this.name = 'ApiError';
  }
}

/**
 * Validation Error - Client-side or server-side validation failure
 */
export class ValidationError extends AppError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, ErrorType.VALIDATION, undefined, undefined, details);
    this.name = 'ValidationError';
  }
}

/**
 * Authentication Error - User not authenticated (401)
 */
export class AuthenticationError extends AppError {
  constructor(message = 'Authentication required. Please log in.') {
    super(message, ErrorType.AUTHENTICATION, 401);
    this.name = 'AuthenticationError';
  }
}

/**
 * Authorization Error - User not authorized for resource (403)
 */
export class AuthorizationError extends AppError {
  constructor(message = 'You do not have permission to access this resource.') {
    super(message, ErrorType.AUTHORIZATION, 403);
    this.name = 'AuthorizationError';
  }
}

/**
 * Not Found Error - Resource not found (404)
 */
export class NotFoundError extends AppError {
  constructor(message = 'The requested resource was not found.', endpoint?: string) {
    super(message, ErrorType.NOT_FOUND, 404, endpoint);
    this.name = 'NotFoundError';
  }
}

/**
 * Server Error - Internal server error (500+)
 */
export class ServerError extends AppError {
  constructor(
    message = 'An internal server error occurred. Please try again later.',
    statusCode = 500,
    endpoint?: string,
  ) {
    super(message, ErrorType.SERVER, statusCode, endpoint);
    this.name = 'ServerError';
  }
}

/**
 * Timeout Error - Request took too long
 */
export class TimeoutError extends AppError {
  constructor(message = 'The request timed out. Please try again.', endpoint?: string) {
    super(message, ErrorType.TIMEOUT, 408, endpoint);
    this.name = 'TimeoutError';
  }
}
